/**
 * Front-end enhancement for mea/hubspot-form - Figma "22 contact-page"
 * node 2008-8392, section 2008:8423.
 *
 * THE SERVER ALREADY RENDERED A WORKING FORM. It posts to its own URL,
 * validates on the server, re-renders with the visitor's answers and its
 * messages intact, and announces its result through `role="alert"` /
 * `role="status"` with focus moved to it. Everything in this file is
 * narrowing and shortcutting what is already there:
 *
 *  - conditional fields get hidden when their condition is not met (the
 *    server renders them visible, so with this file blocked they are simply
 *    ordinary optional fields)
 *  - validation runs before the round trip, using the same rules
 *  - the submit goes over fetch so the page does not reload
 *
 * Nothing here builds a control, and nothing here is the only path to a
 * behaviour. If this file fails to load the form still works.
 *
 * ONE SOURCE FOR THE ACCESSIBLE STATE AND THE VISIBLE STATE
 *
 *  - `aria-invalid` is the state. `style.scss` paints the invalid field off
 *    that attribute, so setting it is the whole operation - there is no
 *    class to keep in step, and a field cannot look wrong while reading as
 *    fine.
 *  - `aria-describedby` is added and removed with the message, so a screen
 *    reader is never pointed at an empty element.
 *  - conditional fields are hidden with the `hidden` attribute, which takes
 *    them out of the accessibility tree as well as the layout, and their
 *    controls are un-`required` at the same moment so a hidden field can
 *    never block a submit invisibly.
 *  - the busy state, the success result and the failure result all go
 *    through the same `role="status"` region rather than being implied by
 *    styling.
 *
 * Markup contract (see render.php):
 *
 *   <form data-mea-hsform data-busy-label data-summary-template data-summary-one>
 *     <p role="status" data-mea-hsform-live>
 *     <div role="alert" tabindex="-1" data-mea-hsform-summary>   (after a failure)
 *     <div class="mea-hsform__field" [data-mea-hsform-when data-mea-hsform-equals]>
 *       <label for="{id}"> <input id="{id}" class="mea-hsform__control">
 *       <p id="{id}-error" data-mea-hsform-error hidden>
 *     <button data-mea-hsform-submit data-label>
 */
import intlTelInput from 'intl-tel-input/intlTelInputWithUtils';
import 'intl-tel-input/styles';

( function () {
	'use strict';

	var PHONE_SHAPE = /^[0-9 ()+.\-]{7,}$/;
	var EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

	/*
	 * One Iti instance per phone control, keyed by the control itself so a
	 * `swapIn()` replacing the whole form (a fresh DOM node) always gets a
	 * fresh instance rather than reusing one built for an element no longer
	 * on the page.
	 */
	var ITI_INSTANCES = new WeakMap();

	/**
	 * Wrap a `type="tel"` control with intl-tel-input, once.
	 *
	 * This is the enhancement layer only: with no JavaScript the control
	 * stays a plain `<input type="tel">` and render.php's own
	 * `mea_hsform_to_e164()` still runs its best-effort US/CA conversion, so
	 * nothing here is load-bearing.
	 *
	 * @param {HTMLElement} control A `type="tel"` control.
	 */
	function initPhone( control ) {
		if ( ITI_INSTANCES.has( control ) ) {
			return;
		}
		ITI_INSTANCES.set(
			control,
			intlTelInput( control, {
				initialCountry: 'us',
				preferredCountries: [ 'us', 'gb', 'ca', 'au' ],
				separateDialCode: true,
			} )
		);
	}

	/**
	 * The two funnel events this file pushes itself, on a real, confirmed
	 * user interaction rather than speculatively. `form_submit_success` and
	 * `form_submit_error` are never pushed from here: they come off
	 * `data-mea-gtm-event`, which render.php only renders once the server has
	 * confirmed the outcome (see `MEA_GTM_FORM_EVENTS` in
	 * `inc/gtm-hooks.php`). Keep this map's values equal to that PHP array's
	 * `start` / `step` entries - the phase 4 GTM plan renames both in one line
	 * per file.
	 */
	var GTM_FORM_EVENTS = {
		start: 'form_start',
		step: 'form_step',
	};

	/**
	 * Push a form funnel event, once, if `window.dataLayer` exists.
	 *
	 * @param {string}      event   The dataLayer event name.
	 * @param {HTMLElement} root    The `.mea-hsform` instance root, for its GTM location.
	 * @param {Object}      payload Extra dataLayer keys from `data-mea-gtm-payload`
	 *                              (form_id, form_name, lead_type, submission_id,
	 *                              user_data, error_type…). Empty for `form_start`/`form_step`.
	 */
	function pushFormEvent( event, root, payload ) {
		if ( ! window.dataLayer || ! event ) {
			return;
		}
		window.dataLayer.push(
			Object.assign(
				{
					event: event,
					form_location: root.getAttribute( 'data-mea-location' ) || '',
				},
				payload || {}
			)
		);
	}

	/**
	 * Push whatever server-confirmed result event is present in this markup,
	 * once. Runs on first load (a no-JS submit that happened to reload with
	 * JavaScript now present) and again after `swapIn` replaces the form with
	 * the POST response, which is the normal enhanced path. render.php omits
	 * `data-mea-gtm-event` entirely on a replayed or honeypot-caught result,
	 * so there is nothing here to push on either of those.
	 *
	 * @param {HTMLElement} root The `.mea-hsform` instance root.
	 */
	function pushConfirmedResult( root ) {
		const el = root.querySelector( '[data-mea-gtm-event]' );
		if ( ! el ) {
			return;
		}
		let payload = {};
		const raw = el.getAttribute( 'data-mea-gtm-payload' );
		if ( raw ) {
			try {
				payload = JSON.parse( raw );
			} catch ( e ) {
				payload = {};
			}
		}
		pushFormEvent( el.getAttribute( 'data-mea-gtm-event' ), root, payload );
		// The payload has been read. Do not leave user_data sitting in the DOM
		// for the rest of the page's life where a session recorder can read it.
		el.removeAttribute( 'data-mea-gtm-payload' );
	}

	/**
	 * Fill {token} placeholders in an editable copy string.
	 *
	 * @param {string} template Copy from a block attribute.
	 * @param {Object} values   Token name to replacement value.
	 * @return {string} The filled string.
	 */
	function fill( template, values ) {
		return Object.keys( values ).reduce( function ( text, token ) {
			return text.split( '{' + token + '}' ).join( values[ token ] );
		}, template || '' );
	}

	/**
	 * The field wrapper a control belongs to.
	 *
	 * @param {HTMLElement} control An input, select or textarea.
	 * @return {HTMLElement|null} Its wrapper.
	 */
	function fieldOf( control ) {
		return control.closest( '.mea-hsform__field' );
	}

	/**
	 * Read the message a control should show, if any.
	 *
	 * The rules mirror `mea_hsform_validate_value()` in render.php, and the
	 * copy comes off the same data attributes the server rendered from its
	 * attributes, so neither side carries a sentence of its own.
	 *
	 * @param {HTMLElement} control An input, select or textarea.
	 * @return {string} The message, or '' when the value is acceptable.
	 */
	function problemWith( control ) {
		var required = control.hasAttribute( 'required' );

		if ( 'checkbox' === control.type ) {
			return required && ! control.checked
				? control.getAttribute( 'data-required-message' ) || ''
				: '';
		}

		var value = ( control.value || '' ).trim();

		if ( required && '' === value ) {
			return control.getAttribute( 'data-required-message' ) || '';
		}

		if ( '' === value ) {
			return '';
		}

		var invalid = control.getAttribute( 'data-invalid-message' ) || '';

		if ( 'email' === control.type ) {
			if ( ! EMAIL_SHAPE.test( value ) ) {
				return invalid;
			}

			// The work-email rule, mirroring render.php's own check. The
			// list and the sentence are the server's, read off the control,
			// so this can only ever refuse what the server would refuse.
			var blockedMessage =
				control.getAttribute( 'data-blocked-domain-message' ) || '';
			var blocked = (
				control.getAttribute( 'data-blocked-domains' ) || ''
			)
				.split( ' ' )
				.filter( Boolean );
			if ( blockedMessage && blocked.length ) {
				var at = value.lastIndexOf( '@' );
				var domain =
					at === -1
						? ''
						: value
								.slice( at + 1 )
								.toLowerCase()
								.replace( /\.+$/, '' );
				if ( domain && blocked.indexOf( domain ) !== -1 ) {
					return blockedMessage.split( '{domain}' ).join( domain );
				}
			}
		}

		if ( 'tel' === control.type ) {
			var iti = ITI_INSTANCES.get( control );
			// The rules mirror mea_hsform_to_e164()'s own US/CA-only reach
			// only when intl-tel-input never attached (script blocked or
			// failed to load); once it has, its own per-country rules are
			// what render.php will actually receive, so they are what has
			// to agree here.
			if ( iti ) {
				if ( ! iti.isValidNumber() ) {
					return invalid;
				}
			} else {
				var digits = value.replace( /[^0-9]/g, '' ).length;
				if ( ! PHONE_SHAPE.test( value ) || digits < 7 ) {
					return invalid;
				}
			}
		}

		if ( 'number' === control.type && isNaN( Number( value ) ) ) {
			return invalid;
		}

		return '';
	}

	/**
	 * Show or clear one control's message.
	 *
	 * `aria-invalid` is the single source of truth: the stylesheet paints
	 * off it, so this one attribute drives both what is seen and what is
	 * announced.
	 *
	 * @param {HTMLElement} control An input, select or textarea.
	 * @param {string}      message The message, or '' to clear.
	 */
	function setProblem( control, message ) {
		var field = fieldOf( control );
		var el = field ? field.querySelector( '[data-mea-hsform-error]' ) : null;

		if ( ! el ) {
			return;
		}

		var described = ( control.getAttribute( 'aria-describedby' ) || '' )
			.split( /\s+/ )
			.filter( function ( id ) {
				return id && id !== el.id;
			} );

		if ( message ) {
			control.setAttribute( 'aria-invalid', 'true' );
			el.textContent = message;
			el.removeAttribute( 'hidden' );
			// The problem is read before the standing hint.
			described.unshift( el.id );
		} else {
			control.removeAttribute( 'aria-invalid' );
			el.textContent = '';
			el.setAttribute( 'hidden', '' );
		}

		if ( described.length ) {
			control.setAttribute( 'aria-describedby', described.join( ' ' ) );
		} else {
			control.removeAttribute( 'aria-describedby' );
		}
	}

	/**
	 * Wire one block instance. Several can share a page.
	 *
	 * @param {HTMLElement} form The form element.
	 */
	function setup( form ) {
		var root = form.closest( '.mea-hsform' );
		var live = form.querySelector( '[data-mea-hsform-live]' );
		var submit = form.querySelector( '[data-mea-hsform-submit]' );
		var submitLabel = form.querySelector( '[data-mea-hsform-submit-label]' );
		var conditionals = Array.prototype.slice.call(
			form.querySelectorAll( '[data-mea-hsform-when]' )
		);
		// On the instance root, not in this closure: `swapIn` re-runs `setup`
		// on the replaced form after a server-rejected submit, and a second
		// `form_start` in one pageview would inflate the funnel by the error
		// rate. `root` survives `swapIn` (it replaces only `.mea-hsform__body`),
		// so the flag persists across the round trip.
		var startFlag = root || form;

		/**
		 * `form_start`, pushed once per pageview on the first real interaction
		 * (a genuine focus, not the field programmatically focused on page
		 * load), even across a `swapIn` re-`setup()` after a failed submit.
		 */
		function noteStart() {
			if ( startFlag.dataset.meaGtmStarted ) {
				return;
			}
			startFlag.dataset.meaGtmStarted = '1';
			pushFormEvent( GTM_FORM_EVENTS.start, root );
		}

		/**
		 * Every control the form owns, honeypot excluded.
		 *
		 * @return {Array} Controls.
		 */
		function controls() {
			return Array.prototype.slice
				.call( form.querySelectorAll( '.mea-hsform__control, .mea-hsform__checkbox' ) )
				.filter( function ( control ) {
					return ! control.closest( '.mea-hsform__trap' );
				} );
		}

		/**
		 * Apply the conditional rules.
		 *
		 * A field that is not on screen is not required, so the two move
		 * together and a hidden field can never block a submit invisibly.
		 * The comparison is the one `mea_hsform_condition_met()` makes.
		 */
		function applyConditions() {
			conditionals.forEach( function ( field ) {
				var sourceName = field.getAttribute( 'data-mea-hsform-when' );
				var wanted = field.getAttribute( 'data-mea-hsform-equals' ) || '';
				var source = form.elements[ sourceName ];

				if ( ! source ) {
					return;
				}

				var actual = '';
				if ( window.RadioNodeList && source instanceof window.RadioNodeList ) {
					var picked = Array.prototype.slice.call( source ).filter( function ( one ) {
						return one.checked;
					} )[ 0 ];
					actual = picked ? picked.value : '';
				} else {
					actual = source.value || '';
				}

				var applies = actual === wanted;
				var owned = Array.prototype.slice.call(
					field.querySelectorAll( 'input, select, textarea' )
				);

				if ( applies ) {
					field.removeAttribute( 'hidden' );
					owned.forEach( function ( control ) {
						if ( 'true' === control.getAttribute( 'data-was-required' ) ) {
							control.setAttribute( 'required', '' );
							control.setAttribute( 'aria-required', 'true' );
						}
					} );
				} else {
					field.setAttribute( 'hidden', '' );
					owned.forEach( function ( control ) {
						if ( control.hasAttribute( 'required' ) ) {
							control.setAttribute( 'data-was-required', 'true' );
							control.removeAttribute( 'required' );
							control.removeAttribute( 'aria-required' );
						}
						// A field nobody can see must not still be shouting.
						setProblem( control, '' );
					} );
				}
			} );
		}

		/**
		 * Announce something through the form's live region.
		 *
		 * @param {string} message What happened.
		 */
		function announce( message ) {
			if ( live ) {
				live.textContent = message;
			}
		}

		/**
		 * Replace the form with a server-rendered result.
		 *
		 * The markup comes from render.php on the other side of the round
		 * trip, so the enhanced path and the no-JavaScript path show the
		 * identical result element rather than two versions of one.
		 *
		 * @param {string} html A whole document from the POST response.
		 */
		function swapIn( html ) {
			var parsed = new window.DOMParser().parseFromString( html, 'text/html' );
			var fresh = parsed.querySelector( '.mea-hsform__body' );
			var body = root ? root.querySelector( '.mea-hsform__body' ) : null;

			if ( ! fresh || ! body ) {
				// Nothing recognisable came back. Fall through to a real
				// navigation rather than leaving the visitor with nothing.
				form.submit();
				return;
			}

			// intl-tel-input attaches its own document-level listeners (the
			// dropdown's click-outside close) that outlive the DOM node they
			// were built for once this body is discarded below.
			Array.prototype.slice
				.call( body.querySelectorAll( '.mea-hsform__control' ) )
				.forEach( function ( control ) {
					var iti = ITI_INSTANCES.get( control );
					if ( iti ) {
						iti.destroy();
						ITI_INSTANCES.delete( control );
					}
				} );

			body.replaceWith( fresh );

			var landing =
				fresh.querySelector( '[data-mea-hsform-result]' ) ||
				fresh.querySelector( '[data-mea-hsform-summary]' );

			if ( landing ) {
				landing.focus();
			}

			if ( root ) {
				pushConfirmedResult( root );
			}

			var again = fresh.querySelector( '[data-mea-hsform]' );
			if ( again ) {
				setup( again );
			}
		}

		/**
		 * Set or clear the in-flight state.
		 *
		 * The button stays focusable throughout - only pointer events stop -
		 * and the state is announced rather than implied by the styling.
		 *
		 * @param {boolean} busy Whether a submit is in flight.
		 */
		function setBusy( busy ) {
			form.setAttribute( 'aria-busy', busy ? 'true' : 'false' );

			if ( ! submit ) {
				return;
			}

			submit.setAttribute( 'aria-busy', busy ? 'true' : 'false' );

			if ( submitLabel ) {
				var busyLabel = form.getAttribute( 'data-busy-label' ) || '';
				submitLabel.textContent = busy && busyLabel
					? busyLabel
					: submit.getAttribute( 'data-label' ) || submitLabel.textContent;
			}
		}

		controls().forEach( function ( control ) {
			if ( 'tel' === control.type ) {
				initPhone( control );
			}

			// The first real keystroke or focus anywhere in the form is a
			// confirmed start, never assumed from the page merely loading.
			control.addEventListener( 'focus', noteStart );
			control.addEventListener( 'input', noteStart );

			// Validate on the way out of a field, never on every keystroke:
			// telling someone their email is wrong while they are still
			// typing it is noise, and it is noise a screen reader repeats.
			control.addEventListener( 'blur', function () {
				setProblem( control, problemWith( control ) );
			} );

			// Once a field is marked invalid, correcting it clears the mark
			// immediately rather than making the visitor leave and return.
			control.addEventListener( 'input', function () {
				if ( 'true' === control.getAttribute( 'aria-invalid' ) ) {
					setProblem( control, problemWith( control ) );
				}
			} );

			// `form_step`: a real conditional-field reveal driven by the
			// visitor picking a value this control actually gates, not the
			// identical call `applyConditions()` below makes once on load to
			// set the initial state.
			control.addEventListener( 'change', function () {
				applyConditions();
				var gatesAStep = conditionals.some( function ( field ) {
					return field.getAttribute( 'data-mea-hsform-when' ) === control.name;
				} );
				if ( gatesAStep ) {
					pushFormEvent( GTM_FORM_EVENTS.step, root );
				}
			} );
		} );

		applyConditions();

		form.addEventListener( 'submit', function ( event ) {
			var bad = controls().filter( function ( control ) {
				if ( control.closest( '[hidden]' ) ) {
					return false;
				}
				var message = problemWith( control );
				setProblem( control, message );
				return '' !== message;
			} );

			if ( bad.length ) {
				event.preventDefault();

				var template = bad.length === 1
					? form.getAttribute( 'data-summary-one' ) || ''
					: fill( form.getAttribute( 'data-summary-template' ) || '', {
						count: bad.length,
					} );

				announce( template );
				bad[ 0 ].focus();
				return;
			}

			// The values are valid, so any phone control intl-tel-input is
			// bound to has already passed `iti.isValidNumber()` above. Write
			// its E.164 form back into the same control HubSpot's `phone`
			// field is read from, rather than whatever partial digits the
			// visitor was still typing.
			controls().forEach( function ( control ) {
				if ( 'tel' !== control.type ) {
					return;
				}
				var iti = ITI_INSTANCES.get( control );
				if ( iti && control.value.trim() ) {
					control.value = iti.getNumber( intlTelInput.NUMBER_FORMAT.E164 );
				}
			} );

			// The values are valid. Hand the round trip to fetch so the page
			// does not reload; the server still does the real validation and
			// the real delivery.
			if ( ! window.fetch || ! window.FormData || ! window.DOMParser ) {
				return; // Let the browser navigate. Same outcome, one reload.
			}

			event.preventDefault();
			setBusy( true );
			announce( form.getAttribute( 'data-busy-label' ) || '' );

			window
				.fetch( window.location.href, {
					method: 'POST',
					body: new FormData( form ),
					credentials: 'same-origin',
					headers: { 'X-Requested-With': 'XMLHttpRequest' },
				} )
				.then( function ( response ) {
					return response.text();
				} )
				.then( function ( html ) {
					setBusy( false );
					swapIn( html );
				} )
				.catch( function () {
					// The network failed rather than the form. Let the
					// browser do the submit properly so the visitor still
					// gets a server-rendered answer.
					setBusy( false );
					form.submit();
				} );
		} );
	}

	function init() {
		Array.prototype.forEach.call(
			document.querySelectorAll( '[data-mea-hsform]' ),
			setup
		);

		// A no-JS submit that reloaded with JS now present can land here
		// already showing a server-confirmed success or error result.
		Array.prototype.forEach.call(
			document.querySelectorAll( '.mea-hsform' ),
			pushConfirmedResult
		);
	}

	if ( 'loading' === document.readyState ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
}() );
